import { getDesignGeneration, type TPageTransitionResolver } from '@libs/core';
import { ActionSystemActionType, ActionTypeEnum } from '@libs/types';
import { getSemanticColors, SemanticColor } from '../theme/colors';
// Global `::view-transition-*(.page-…)` rules for the page `<ViewTransition>` classes.
import './pageTransitions.css';
import { TUiEffects, TUiEffectsAction, TUiEffectsCarrier, UiTransitionDirection, UiTransitionOrientation } from './types';

/** Local CSS variables of the transition CSS; the colours come from semantic tokens, never named in the CSS. */
const DIM_VAR = '--page-transition-dim';
const BG_VAR = '--page-transition-bg';

export type TPageTransition = {
    direction: UiTransitionDirection;
    orientation: UiTransitionOrientation;
};

const isDirection = (value: unknown): value is UiTransitionDirection => Object.values<unknown>(UiTransitionDirection).includes(value);
const isOrientation = (value: unknown): value is UiTransitionOrientation => Object.values<unknown>(UiTransitionOrientation).includes(value);

const isNavigateBack = (action: TUiEffectsAction | undefined) => !!action?.systemActionType?.includes(ActionSystemActionType.navigateBack);

/**
 * Picks the page-change animation, field by field:
 * - direction: `action.uiEffects` → `back` for a `navigateBack` action (the page default never repaints going back
 *   as forward) → the page's `uiEffects` → `forward`;
 * - orientation: `action.uiEffects` → the page's `uiEffects` → `horizontal`.
 * Unknown values count as missing. Without `uiEffects` on both the action and the page there is no animation;
 * `Player` actions never navigate, so they get none either.
 */
export const resolvePageTransition = (action: TUiEffectsAction | undefined, pageUiEffects: TUiEffects | undefined): TPageTransition | undefined => {
    if (action?.type === ActionTypeEnum.Player) return undefined;
    const actionTransition = action?.uiEffects?.transition;
    const pageTransition = pageUiEffects?.transition;
    if (!actionTransition && !pageTransition) return undefined;

    const actionDirection = isDirection(actionTransition?.direction) ? actionTransition.direction : undefined;
    const backDirection = isNavigateBack(action) ? UiTransitionDirection.back : undefined;
    const pageDirection = isDirection(pageTransition?.direction) ? pageTransition.direction : undefined;
    const actionOrientation = isOrientation(actionTransition?.orientation) ? actionTransition.orientation : undefined;
    const pageOrientation = isOrientation(pageTransition?.orientation) ? pageTransition.orientation : undefined;

    return {
        direction: actionDirection ?? backDirection ?? pageDirection ?? UiTransitionDirection.forward,
        orientation: actionOrientation ?? pageOrientation ?? UiTransitionOrientation.horizontal,
    };
};

/** Transition type and View Transition Class of a page change: `page-forward-horizontal`, … */
export const getPageTransitionType = ({ direction, orientation }: TPageTransition) => `page-${direction}-${orientation}`;

/**
 * `update` classes of the page `<ViewTransition>`: each transition type maps to the class of the same name, so
 * `pageTransitions.css` styles it; anything else (an update without our type) doesn't animate.
 */
export const PAGE_TRANSITION_CLASSES = Object.fromEntries(
    Object.values(UiTransitionDirection).flatMap((direction) =>
        Object.values(UiTransitionOrientation).map((orientation) => {
            const type = getPageTransitionType({ direction, orientation });
            return [type, type];
        }),
    ),
) as Record<string, string>;

/**
 * Consumer's page-transition resolver for the core: the transition type for this page change, or nothing when
 * there are no `uiEffects`. Also fills the colours of the transition CSS: the dim (`OverlayDarkening` of the
 * current design) and the page background.
 */
export { default as pageTransitionStyles } from './pageTransition.module.css';

export const resolvePageTransitionTypes: TPageTransitionResolver = ({ action, leftPage }) => {
    const transition = resolvePageTransition(action, (leftPage as TUiEffectsCarrier | undefined)?.uiEffects);
    if (!transition) return undefined;
    const root = document.documentElement.style;
    root.setProperty(DIM_VAR, getSemanticColors(getDesignGeneration())[SemanticColor.OverlayDarkening]);
    // The page background behind the widgets is the body's, which is always the v1 design.
    root.setProperty(BG_VAR, getSemanticColors()[SemanticColor.BgPrimary]);
    return [getPageTransitionType(transition)];
};
